import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { styled, useTheme } from '@mui/material/styles';
import {
  AppBar,
  Avatar,
  Box,
  CssBaseline,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Logout as LogoutIcon,
  AccountCircle as AccountCircleIcon,
  Image as ImageIcon,
  LocalOffer as LocalOfferIcon,
  LocalDrink as LocalDrinkIcon,
  NewReleases as NewReleasesIcon,
  Notifications as NotificationsIcon,
  Business as BusinessIcon,
  PeopleAlt as PeopleAltIcon,
  Inventory as InventoryIcon,
  Settings as SettingsIcon,
  ExpandLess,
  ExpandMore,
  Map as MapIcon,
  Build as BuildIcon,
} from '@mui/icons-material';
import { Collapse, SvgIcon } from '@mui/material';
import StorefrontIcon from '@mui/icons-material/Storefront';


const drawerWidth = 240;

const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })<{
  open?: boolean;
}>(({ theme, open }) => ({
  flexGrow: 1,
  padding: theme.spacing(3),
  transition: theme.transitions.create('margin', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginLeft: `-${drawerWidth}px`,
  ...(open && {
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginLeft: 0,
  }),
}));

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
  justifyContent: 'space-between',
}));

const menuItems = [
  { text: 'Dashboard', icon: <DashboardIcon />, path: '/' },
  { text: 'Promociones', icon: <LocalOfferIcon />, path: '/promociones' },
  { text: 'Novedades', icon: <NewReleasesIcon />, path: '/novedades' },
  { text: 'Notificaciones', icon: <NotificationsIcon />, path: '/notificaciones' },
  { 
    text: 'Clientes', 
    icon: <StorefrontIcon />, 
    path: '/clientes',
    subItems: [
      { text: 'Lista de Clientes', path: '/clientes' },
      { text: 'Puntos Clientes', path: '/clientes/puntos-clientes' }
    ]
  },
  { text: 'Consumidores', icon: <PeopleAltIcon />, path: '/consumidores' },
  {
    text: 'WIP',
    icon: <BuildIcon />,
    path: '/wip',
    subItems: [
      { text: 'GIS', path: '/wip/gis' },
    ]
  },
  { 
    text: 'Configuración', 
    icon: <SettingsIcon />, 
    path: '/configuracion',
    subItems: [
      { text: 'Usuarios', path: '/configuracion/usuarios' },
      { text: 'Mantenimiento', path: '/configuracion/mantenimiento' },
      { text: 'Procesos', path: '/configuracion/procesos' },
      { text: 'Versiones App', path: '/configuracion/versiones-app' }
    ]
  },
];

const DashboardLayout: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { currentUser, logout, userProfile } = useAuth();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [open, setOpen] = useState(!isMobile);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [expandedItems, setExpandedItems] = useState<{[key: string]: boolean}>({});
  const location = useLocation();

  // Filtrar menú según el perfil del usuario
  const filteredMenuItems = React.useMemo(() => {
    if (userProfile === 'Root') {
      return menuItems; // Mostrar todo el menú para Root
    }
    // Ocultar WIP y Configuración para otros perfiles
    return menuItems.filter(item => item.text !== 'WIP' && item.text !== 'Configuración');
  }, [userProfile]);

  // Efecto para manejar la expansión automática de los ítems del menú
  React.useEffect(() => {
    // Encontrar el ítem del menú que coincide con la ruta actual
    const findActiveMenu = () => {
      const newExpandedItems: {[key: string]: boolean} = {};
      
      filteredMenuItems.forEach(item => {
        if (item.subItems) {
          // Verificar si alguna subpágina está activa
          const isSubItemActive = item.subItems.some(subItem => 
            location.pathname === subItem.path || 
            (location.pathname + '/') === subItem.path ||
            location.pathname.startsWith(`${subItem.path}/`)
          );
          
          if (isSubItemActive) {
            newExpandedItems[item.text] = true;
          }
        }
      });
      
      setExpandedItems(prev => ({
        ...prev,
        ...newExpandedItems
      }));
    };
    
    findActiveMenu();
  }, [location.pathname]);

  // Check if a menu item is active
  const isActive = (path: string, subItems?: { path: string }[]) => {
    // Si tiene subitems, verificar que no haya un match más específico
    if (subItems && subItems.length > 0) {
      // Si algún subitem coincide exactamente, el padre no debe estar activo
      const hasExactSubItemMatch = subItems.some(subItem => location.pathname === subItem.path);
      if (hasExactSubItemMatch) {
        return false;
      }
      // Si algún subitem es una subruta, el padre no debe estar activo
      const hasSubItemMatch = subItems.some(subItem => 
        location.pathname.startsWith(`${subItem.path}/`)
      );
      if (hasSubItemMatch) {
        return false;
      }
    }
    
    // Comparación exacta para la ruta
    return location.pathname === path;
  };

  // Check if a submenu item is active
  const isSubmenuActive = (subItems: { path: string }[] = []) => {
    return subItems.some(item => 
      location.pathname === item.path || 
      location.pathname.startsWith(`${item.path}/`)
    );
  };

  const handleSubmenuToggle = (itemText: string) => (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setExpandedItems(prev => ({
      ...prev,
      [itemText]: !prev[itemText]
    }));
  };

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleAvatarClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
    }
  };

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
          ...(open && {
            marginLeft: drawerWidth,
            width: `calc(100% - ${drawerWidth}px)`,
            transition: theme.transitions.create(['width', 'margin'], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
          }),
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerOpen}
            edge="start"
            sx={{
              marginRight: 2,
              ...(open && { display: 'none' }),
            }}
          >
            <MenuIcon />
          </IconButton>
          
          {/* Logo */}
          <Box
            component="a"
            href="/"
            sx={{
              display: 'flex',
              alignItems: 'center',
              textDecoration: 'none',
              '&:hover': {
                opacity: 0.9,
              },
            }}
          >
            <Box
              component="img"
              src="/images/logo_blanco.png"
              alt="Logo AdminPro"
              sx={{
                height: { xs: 28, sm: 35 }, // 70% del tamaño original (40*0.7=28, 50*0.7=35)
                width: 'auto',
                transition: 'transform 0.3s ease',
                '&:hover': {
                  transform: 'scale(1.03)',
                },
              }}
            />
          </Box>
          
          <Box sx={{ flexGrow: 1 }} />
          
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {currentUser && (
              <>
                <Tooltip title="Cuenta">
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar
                      onClick={handleAvatarClick}
                      alt={currentUser.displayName || currentUser.email || 'User'}
                      src={currentUser.photoURL || ''}
                      sx={{ 
                        width: 32, 
                        height: 32, 
                        cursor: 'pointer',
                        ml: 2,
                        '&:hover': {
                          opacity: 0.8,
                        }
                      }}
                    >
                      {currentUser.email?.charAt(0).toUpperCase()}
                    </Avatar>
                  </Box>
                </Tooltip>
                <Menu
                  id="menu-appbar"
                  anchorEl={anchorEl}
                  anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  keepMounted
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  open={Boolean(anchorEl)}
                  onClose={handleClose}
                >
                  <MenuItem onClick={handleClose}>
                    <ListItemIcon>
                      <AccountCircleIcon fontSize="small" />
                    </ListItemIcon>
                    <Typography variant="inherit" noWrap>
                      {currentUser.email}
                    </Typography>
                  </MenuItem>
                  <MenuItem onClick={handleLogout}>
                    <ListItemIcon>
                      <LogoutIcon fontSize="small" />
                    </ListItemIcon>
                    <Typography variant="inherit" noWrap>
                      Cerrar sesión
                    </Typography>
                  </MenuItem>
                </Menu>
              </>
            )}
          </Box>
        </Toolbar>
      </AppBar>
      <Drawer
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
          },
        }}
        variant={isMobile ? 'temporary' : 'persistent'}
        anchor="left"
        open={open}
        onClose={handleDrawerClose}
      >
        <DrawerHeader>
          <Typography variant="h6" noWrap component="div" sx={{ ml: 2, fontWeight: 'bold' }}>
            Backoffice
          </Typography>
          <IconButton onClick={handleDrawerClose}>
            {theme.direction === 'ltr' ? <ChevronLeftIcon /> : <ChevronRightIcon />}
          </IconButton>
        </DrawerHeader>
        <Divider />
        <List>
          {filteredMenuItems.map((item) => (
            <React.Fragment key={item.text}>
              <ListItem disablePadding>
                <ListItemButton 
                  component={item.subItems ? 'div' : 'a'}
                  href={item.subItems ? '#' : item.path}
                  onClick={item.subItems ? handleSubmenuToggle(item.text) : undefined}
                  sx={{
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                      '& .MuiListItemText-primary': {
                        color: 'inherit',
                      },
                    },
                    // Solo aplicar estilo activo si es un ítem sin subitems o si es la ruta exacta
                    ...(item.subItems ? {
                      // Para ítems con subitems, solo resaltar si está activo y no tiene subitems activos
                      ...(isActive(item.path, item.subItems) && !isSubmenuActive(item.subItems) && {
                        backgroundColor: 'rgba(200, 16, 46, 0.1)',
                        borderRight: '3px solid #C8102E',
                        '& .MuiListItemIcon-root': {
                          color: '#C8102E',
                        },
                        '& .MuiListItemText-primary': {
                          fontWeight: 'bold',
                          color: '#C8102E',
                        },
                      }),
                      // Estilo para cuando hay subitems activos
                      ...(isSubmenuActive(item.subItems) && {
                        backgroundColor: 'transparent',
                        '& .MuiListItemIcon-root': {
                          color: 'inherit',
                        },
                        '& .MuiListItemText-primary': {
                          fontWeight: 'normal',
                          color: 'inherit',
                        },
                      })
                    } : {
                      // Para ítems sin subitems, resaltar normalmente
                      ...(isActive(item.path) && {
                        backgroundColor: 'rgba(200, 16, 46, 0.1)',
                        borderRight: '3px solid #C8102E',
                        '& .MuiListItemIcon-root': {
                          color: '#C8102E',
                        },
                        '& .MuiListItemText-primary': {
                          fontWeight: 'bold',
                          color: '#C8102E',
                        },
                      })
                    }),
                  }}
                >
                  <ListItemIcon sx={{
                    ...(isActive(item.path, item.subItems) && { color: '#C8102E' })
                  }}>{item.icon}</ListItemIcon>
                  <ListItemText 
                    primary={item.text} 
                    primaryTypographyProps={{
                      fontWeight: isActive(item.path, item.subItems) ? 'bold' : 'normal',
                      color: isActive(item.path, item.subItems) ? '#C8102E' : 'inherit',
                    }}
                  />
                  {item.subItems && (
                    <Box component="span" sx={{ ml: 'auto' }}>
                      {expandedItems[item.text] ? <ExpandLess /> : <ExpandMore />}
                    </Box>
                  )}
                </ListItemButton>
              </ListItem>
              {item.subItems && (
                <Collapse in={expandedItems[item.text] || isSubmenuActive(item.subItems)} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {item.subItems.map((subItem) => (
                      <ListItem key={subItem.text} disablePadding sx={{ pl: 4 }}>
                        <ListItemButton 
                          component="a" 
                          href={subItem.path}
                          sx={{
                            '&:hover': {
                              backgroundColor: 'rgba(0, 0, 0, 0.04)',
                              '& .MuiListItemText-primary': {
                                color: 'inherit',
                              },
                            },
                            ...(isActive(subItem.path) ? {
                              backgroundColor: 'rgba(200, 16, 46, 0.1)',
                              '& .MuiListItemText-primary': {
                                fontWeight: 'bold',
                                color: '#C8102E',
                              },
                              '&::before': {
                                content: '""',
                                position: 'absolute',
                                left: 0,
                                top: 0,
                                bottom: 0,
                                width: '3px',
                                backgroundColor: '#C8102E',
                              }
                            } : {
                              // Estilo para subitems inactivos
                              '& .MuiListItemText-primary': {
                                fontWeight: 'normal',
                                color: 'rgba(0, 0, 0, 0.87)',
                              },
                            }),
                          }}
                        >
                          <ListItemText 
                            primary={subItem.text} 
                            primaryTypographyProps={{
                              fontWeight: isActive(subItem.path) ? 'bold' : 'normal',
                              color: isActive(subItem.path) ? '#C8102E' : 'inherit',
                              pl: 1,
                            }}
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              )}
            </React.Fragment>
          ))}
        </List>
        <Box 
          sx={{ 
            mt: 'auto', 
            p: 2, 
            textAlign: 'center',
            position: 'sticky',
            bottom: 0,
            backgroundColor: 'background.paper',
            borderTop: '1px solid',
            borderColor: 'divider'
          }}
        >
          <Typography 
            variant="caption" 
            sx={{ 
              color: 'red', 
              fontWeight: 'bold',
              '&:hover': {
                opacity: 0.8,
              }
            }}
          >
            Powered by B.live
          </Typography>
        </Box>
      </Drawer>
      <Main open={open}>
        <DrawerHeader />
        <Outlet />
      </Main>
    </Box>
  );
};

export default DashboardLayout;
